import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Search, Mail, Gauge } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import host from "../../global";
import { useTheme } from "../../hooks/useTheme";
import { useDispatch } from "react-redux";
import { updateNavigationTab } from "../../store/slices/candidateManagementSlice";
import { useNavigationGuard } from '../../contexts/NavigationGuardContext';
import { useSourcingConfig } from '../../contexts/SourcingConfigContext';

const routeMapping = {
  Home: ["org-admin/dashboard"],
  Reports: ["/org-admin/candidatereports", "/org-admin/evaluationpage"],
  Jobs: ["org-admin/created-jds", "org-admin/editjd" ,"org-admin/jobs/:jobId"],
  "Test Set": [
    "org-admin/testlibrary",
    "org-admin/createcodingproblemset",
    // Substring match, so this covers the library, the builder and the AI flow.
    "org-admin/mcqsets",
    "org-admin/behavioralsets",
  ],
  Team: ["org-admin/hrprofiles"],
  Sourcing: ["org-admin/sourcing$"],
  Outreach: ["org-admin/sourcing/sequences"],
  Activity: ["org-admin/activity"],
  "Sourced Candidates": ["org-admin/sourced-candidates"],
  "Raise Ticket": ["org-admin/raise-ticket"],
};



const SidebarIcon = ({ iconPath, hoverIconPath, Icon, label, path, onClick }) => {
  const location = useLocation();

  // ADD THIS FUNCTION HERE
  const matchesRoute = (pathname, route) => {
    const pattern = route.replace(/:[^\s/]+/g, '[^/]+');
    return new RegExp(pattern).test(pathname);
  };

  const isActive =
    path !== "#" &&
    (routeMapping[label]
      ? routeMapping[label].some((route) => matchesRoute(location.pathname, route))
      : location.pathname.includes(path)); // prefer explicit route mapping when defined

  return (
    <div className="relative flex flex-1 my-1">
      {isActive && (
        <div className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-500 to-blue-500 rounded-l-md bg-bgPrimary "></div>
      )}
      <button
        onClick={onClick}
        className={`flex w-full text-white cursor-pointer rounded-md transition-all group border-none bg-transparent ${
          isActive ? "bg-gradient-to-r from-purple-600/20 to-transparent border-none border-l-2 " : ""
        }`}
      >
        <div className={`flex flex-col items-center justify-center w-full gap-1 px-2`}>
          {Icon ? (
            <Icon
              strokeWidth={1.50}
              className={`w-6 h-6 transition-all ${
                isActive
                  ? "text-purple-500 dark:text-purple-400"
                  : "text-textSecondary dark:text-white group-hover:text-purple-500 dark:group-hover:text-purple-400"
              }`}
            />
          ) : (
            <>
              <img
                src={isActive ? hoverIconPath : iconPath}
                alt={label}
                className={`transition-all ${
                  label === "Home" && isActive
                    ? "w-6 h-6"
                    : label === "Settings" && label != "Home"
                    ? "w-6 h-6"
                    : "w-6 h-6"
                } group-hover:hidden`}
              />
              <img
                src={hoverIconPath}
                alt={`${label} hover`}
                className={`transition-all ${
                  label === "Home"
                    ? "w-6 h-6"
                    : label === "Settings" && label != "Home"
                    ? "w-6 h-6"
                    : "w-6 h-6"
                } hidden group-hover:block ${isActive ? "block" : ""}`}
              />
            </>
          )}
          <span
            className={`dark:text-[11px] text-[11px] font-[500] text-center w-full leading-tight dark:opacity-80  ${
              label === "Home" ? "" : "mt-1.5"
            } ${isActive ? "dark:text-purple-400 text-textPrimary" : "dark:text-white text-textPrimary"}`}
          >
            {label}
          </span>
        </div>
      </button>
    </div>
  );
};

const Sidebar = ({ handleSignOut }) => {
  const {theme} = useTheme();
  const accessToken = sessionStorage.getItem("accessToken");
  const [notification, setNotification] = useState([]);
  const { guardedNavigate } = useNavigationGuard();
  const { sourcingEnabled } = useSourcingConfig();

  const dispatch = useDispatch();

  const sidebarItems = [
    // {
    //   iconPath: "/notificationbell.png",
    //   hoverIconPath: "/notificationbellhover.png",
    //   label: "Activity",
    //   path: "activity",
    // },
    {
      iconPath:`${theme === 'dark' ?  "/homesidebar.png":"/homeSVG.svg"}`,
      hoverIconPath: "/home_hover.png",
      label: "Home",
      path: "dashboard",
    },
    // {
    //   iconPath: "/reportssidebar.png",
    //   hoverIconPath: "/reportssidebarhover.png",
    //   label: "Reports",
    //   path: "candidatereports",
    // },
    {
      iconPath: `${theme === 'dark' ? "/joblibrarysidebar.png" : "/jobsSVG.svg"}`,
      hoverIconPath: "/joblibrarysidebarhover.png",
      label: "Jobs",
      path: "created-jds",
    },
    // {
    //   Icon: Search,
    //   label: "Sourcing",
    //   path: "sourcing",
    // },
    {
      Icon: Mail,
      label: "Outreach",
      path: "sourcing/sequences",
    },
    {
      iconPath:`${theme === 'dark' ?  "/testsetsidebar.png":"/testSetSvg.svg"}`,
      hoverIconPath: "/testsetsidebarhover.png",
      label: "Test Set",
      path: "testlibrary",
    },
    {
      iconPath: `${theme === 'dark' ? "/teamsidebar.png":"/teamsSVG.svg"}`,
      hoverIconPath: "/teamsidebarhover.png",
      label: "Team",
      path: "hrprofiles",
    },
    {
      Icon: Gauge,
      label: "Usage",
      path: "usage",
    },
    {
      iconPath: `${theme === 'dark' ? '/helpDarkSVG.svg' : '/helpSVG.svg'}`,
      hoverIconPath: "/helpFilledSVG.svg",
      label: "Help",
      path: "tutorial-videos",
    },
    {
      iconPath: `${theme === 'dark' ? "/setting_normal.png" : '/settingsSVG.svg'}`,
      hoverIconPath: "/setting_hover.png",
      label: "Settings",
      path: "configurations",
    },
  ];

  const fetchBulkResumeUploadResult = async () => {
    try {
      const response = await axios.get(`${host}/resumes/bulk`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        withCredentials: true,
        params: {
          jd_id: "all"
        }
      });
      const results = response.data.notifications || [];
      results.sort((a, b) => {
        if (a.bulk_upload_date === null && b.bulk_upload_date === null)
          return 0;
        if (a.bulk_upload_date === null) return 1;
        if (b.bulk_upload_date === null) return -1;
        return new Date(b.bulk_upload_date) - new Date(a.bulk_upload_date);
      });
      setNotification(results);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          await new Promise((resolve) => {
            sessionStorage.removeItem("accessToken");
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  useEffect(() => {
    fetchBulkResumeUploadResult();

    // Event listener for notification read status update
    const handleNotificationReadStatusUpdated = () => {
      fetchBulkResumeUploadResult(); // Re-fetch notifications when event is triggered
    };

    window.addEventListener(
      "notificationReadStatusUpdated",
      handleNotificationReadStatusUpdated
    );

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener(
        "notificationReadStatusUpdated",
        handleNotificationReadStatusUpdated
      );
    };
  }, [accessToken]);

  const unreadNotificationCount = notification.filter(
    (notif) => !notif.read_status
  ).length;

  // Sourcing & Outreach are only available when sourcing is enabled for the org.
  // Hidden until the config confirms sourcing_enabled is true (avoids a flicker).
  const visibleSidebarItems = sidebarItems.filter(
    (item) =>
      (item.label !== "Sourcing" && item.label !== "Outreach") || sourcingEnabled
  );

  return (
    <div
      className="
        dark:bg-app-background border-r dark:border-white/10 bg-bgPrimary border-gray-200 
        transition-all duration-200 ease-in-out w-17 overflow-hidden flex flex-col items-center py-3 min-h-screen "
    >
      <div className="flex flex-col gap-4 items-center justify-center py-2 flex-1 min-h-0">
        <button
          onClick={() => {
            guardedNavigate("/org-admin/dashboard");
            dispatch(updateNavigationTab("ALL"));
          }}
          className="cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img
            src="/Homepagelogo.png"
            alt="Logo"
            style={{ height: "34px", width: "24px" }}
          />
        </button>
        {/* Items share the window's height: full size (88px) when they fit,
            shrinking evenly on short windows (e.g. 1280×720) so Settings
            is never cut off. */}
        <div className="w-full px-2 py-4 flex flex-col flex-1 min-h-0">
          {visibleSidebarItems.map((item, index) => (
            <div
              key={index}
              data-guide={item.path === "configurations" ? "settings" : undefined}
              className="flex flex-1 min-h-[52px] max-h-[88px]"
            >
              {item.label === "Activity" && (
                <div>
                  {unreadNotificationCount > 0 && (
                    <span
                      className="absolute left-[50px] flex items-center justify-center 
                            w-5 h-5 bg-red-600 text-white text-xs font-medium
                            rounded-full border-2 border-gray-900"
                    >
                      {unreadNotificationCount}
                    </span>
                  )}
                </div>
              )}
              <SidebarIcon
                key={index}
                iconPath={item.iconPath}
                hoverIconPath={item.hoverIconPath}
                Icon={item.Icon}
                label={item.label}
                path={item.path}
                onClick={() => {
                  // The Usage page reports where it was opened from
                  guardedNavigate(`/org-admin/${item.path}`,
                    item.path === "usage" ? { state: { from: "sidebar" } } : undefined);
                  dispatch(updateNavigationTab("ALL"));
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
